import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import API from "../Network/API";
import { SITE_CODE_FETCH_SUCCESS } from "../Reducer/siteCodeSlice";
import isTenantUser from "../CustomHook/tenantUser";
import "./SiteCodeMenu.css";

const SUB_LINKS = [
  { to: (id) => `/site/${id}`, end: true, icon: "fa-solid fa-chart-pie", label: "Overview" },
  { to: (id) => `/site/${id}/alarms`, end: false, icon: "fa-regular fa-bell", label: "Alarm History" },
  { to: (id) => `/site/${id}/assets`, end: false, icon: "fa-regular fa-boxes-stacked", label: "Asset History" },
  { to: (id) => `/site/${id}/pm`, end: false, icon: "fa-regular fa-clipboard-check", label: "PM" },
  { to: (id) => `/site/${id}/site_down_history`, end: false, icon: "fa-regular fa-tower-broadcast", label: "Site Down History" },
  { to: (id) => `/site/${id}/dc_energy_meter`, end: false, icon: "fa-regular fa-bolt", label: "DC Energy Meter" },
  { to: (id) => `/site/${id}/configuration`, end: false, icon: "fa-regular fa-cog", label: "Configuration" },
];

const TENANT_SUB_LINKS = [
  { to: (id) => `/site/${id}`, end: true, icon: "fa-solid fa-chart-pie", label: "Overview" },
  { to: (id) => `/site/${id}/alarms/rms`, end: false, icon: "fa-regular fa-bell", label: "Alarm History" },
  { to: (id) => `/site/${id}/dc_energy_meter`, end: false, icon: "fa-regular fa-bolt", label: "DC Energy Meter" },
];

function buildSubLinks(site, tenantUser) {
  if (tenantUser) {
    return TENANT_SUB_LINKS;
  }

  const rectifierLinks = (site?.rectifier ?? []).map((rectif) => ({
    to: (id) => `/site/${id}/rectifier/${rectif.id}`,
    end: false,
    icon: "fa-regular fa-usb-drive",
    label: rectif.rectifier_name+' Rectifier',
  }));

  return [...SUB_LINKS, ...rectifierLinks];
}

function extractSitesFromApi(body) {
  const sites = body?.data?.sites ?? body?.sites;
  if (Array.isArray(sites)) return sites;
  if (Array.isArray(sites?.data)) return sites.data;
  return [];
}

function sortSitesWithActiveFirst(sites, activeSiteId) {
  if (!activeSiteId || !sites?.length) return sites ?? [];

  const list = [...sites];
  const idx = list.findIndex((s) => s.site_id === activeSiteId);
  if (idx <= 0) return list;

  const [active] = list.splice(idx, 1);
  return [active, ...list];
}

export default function SiteCodeMenu() {
  const dispatch = useDispatch();
  const { id: routeSiteId } = useParams();

  const selector = useSelector((state) => state.siteCodeReducer);
  const selectorSite = useSelector((state) => state.siteIdReducer);
  const { user: profileUser } = useSelector((state) => state.profileReducer);
  const { user: authUser } = useSelector((state) => state.authReducer);
  const tenantUser = isTenantUser(profileUser?.id ? profileUser : authUser);
  const sites = selector?.sites ?? [];
  const activeSiteId = routeSiteId || selectorSite?.data || null;

  const [isLoading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const listLoadedRef = useRef(false);
  const searchTimerRef = useRef(null);

  const loadAllSites = useCallback(async (siteIdQuery = "") => {
    const resp = await API.get(
      `/v1/oauth/all_sites?site_id=${encodeURIComponent(siteIdQuery)}`
    );
    return extractSitesFromApi(resp.data);
  }, []);

  useEffect(() => {
    if (listLoadedRef.current && sites.length > 0) return;

    let cancelled = false;

    const init = async () => {
      setLoading(true);
      try {
        const list = await loadAllSites("");
        if (!cancelled && list.length > 0) {
          dispatch(SITE_CODE_FETCH_SUCCESS(list));
          listLoadedRef.current = true;
        }
      } catch (err) {
        if (!cancelled) console.error("SiteCodeMenu: failed to load sites", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();

    return () => {
      cancelled = true;
    };
  }, [dispatch, loadAllSites, sites.length]);

  useEffect(() => {
    if (!activeSiteId || sites.length === 0) return;

    const hasActive = sites.some((s) => s.site_id === activeSiteId);
    if (hasActive) return;

    let cancelled = false;

    (async () => {
      try {
        const extra = await loadAllSites(activeSiteId);
        if (!cancelled && extra[0]) {
          dispatch(SITE_CODE_FETCH_SUCCESS([extra[0], ...sites]));
        }
      } catch (err) {
        if (!cancelled) console.error("SiteCodeMenu: failed to load active site", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSiteId, sites, dispatch, loadAllSites]);

  const displaySites = useMemo(
    () => sortSitesWithActiveFirst(sites, activeSiteId),
    [sites, activeSiteId]
  );

  const handleSearch = (value) => {
    setSearchText(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    searchTimerRef.current = setTimeout(() => {
      setLoading(true);
      loadAllSites(value.trim())
        .then((list) => dispatch(SITE_CODE_FETCH_SUCCESS(list)))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 400);
  };

  useEffect(() => () => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
  }, []);

  const renderSiteMenu = (site, isActive) => (
    <li
      key={site.site_id}
      className={`site-sidebar__item ${isActive ? "site-sidebar__item--active" : ""}`}
    >
      <button
        type="button"
        className="site-sidebar__toggle"
        data-bs-toggle="collapse"
        data-bs-target={`#home-collapse${site.site_id}`}
        aria-expanded={isActive ? "true" : "false"}
      >
        <span className="site-sidebar__site-id">
          <i className="fa-solid fa-tower-broadcast" />
          {site.site_id}
        </span>
        <i className="fa-solid fa-chevron-down site-sidebar__chevron" />
      </button>
      <div className={`collapse ${isActive ? "show" : ""}`} id={`home-collapse${site.site_id}`}>
        <ul className="site-sidebar__subnav">
          {buildSubLinks(site, tenantUser).map((item) => (
            <li key={item.label}>
              <NavLink
                to={item.to(site.site_id)}
                end={item.end}
                className={({ isActive: linkActive }) =>
                  `site-sidebar__link ${linkActive ? "site-sidebar__link--active" : ""}`
                }
              >
                <i className={item.icon} />
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </li>
  );

  return (
    <div className="main_content">
      <aside id="left-sidebar" className="sidebar site-sidebar">
        <div className="site-sidebar__head">
          <h6>
            <i className="fa-solid fa-tower-cell" />
            Site Navigator
          </h6>
          {!isLoading && displaySites.length > 0 ? (
            <span className="site-sidebar__count">{displaySites.length}</span>
          ) : null}
        </div>

        <form className="site-sidebar__search" onSubmit={(e) => e.preventDefault()}>
          <div className="site-sidebar__search-wrap">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search Site ID..."
              autoComplete="off"
              id="search-options"
            />
          </div>
        </form>

        <ul className="site-sidebar__list left-sidebar-menu list-unstyled ps-0">
          {isLoading && (
            <>
              <div className="site-sidebar__skeleton" />
              <div className="site-sidebar__skeleton" />
              <div className="site-sidebar__skeleton" />
            </>
          )}

          {!isLoading && displaySites.length === 0 && (
            <li className="site-sidebar__state">
              <i className="fa-regular fa-map-location-dot" />
              No sites found
            </li>
          )}

          {!isLoading && displaySites.map((site) =>
            renderSiteMenu(site, activeSiteId != null && site.site_id === activeSiteId)
          )}
        </ul>
      </aside>
    </div>
  );
}
